import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WeeklyReport } from '@adhd/shared';
import { api } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { formatWeek, summarise } from '../screens/History.js';

/**
 * The weeks, one row and the rest behind the week page. Lived on the log
 * until 14 Oct 2026; now under the month grid on Days, which is where the
 * record is read back over time. `compact` is the Days rendering: a small
 * heading, no introduction.
 */
/**
 * Weekly recaps, which until now had no screen at all.
 *
 * The endpoints have existed since the report was written and nothing ever
 * called them, so the only place a recap could be read was a share link: a
 * clinician could read the owner's week and the owner could not.
 *
 * ONE REPORT, NOT TWO. The daily read needs a separate owner-only version
 * because the shared one is deliberately never shown the entries. This is the
 * opposite case — the weekly report is written FROM the entries and already
 * travels under the `summary` scope, which the share screen says out loud. So
 * there is nothing to split, and the same report is simply shown to the person
 * it is about.
 *
 * It does show two things the share view drops. `highlights` and `patterns` are
 * generated every time and rendered nowhere, and they are the parts that
 * actually serve the purpose the report was built for — ADHD time-blindness
 * makes last Tuesday genuinely inaccessible, and a list of what happened is
 * more use for that than a paragraph about it.
 */
export function WeeklyRecaps({ compact = false }: { compact?: boolean } = {}): JSX.Element | null {
  const [reports, setReports] = useState<WeeklyReport[] | null>(null);
  const { canSummarise } = useAi();

  /*
   * No button any more — the server writes the last finished week when this
   * asks for the list.
   *
   * It used to be a chore: "Write up the last finished week", at the foot of the
   * Log, for the one feature built precisely for the thing ADHD does to you. The
   * whole premise is that last Tuesday is unreachable, and the app was asking
   * the person who cannot hold last Tuesday to remember to press a button about
   * it every Monday.
   */
  useEffect(() => {
    api
      .weeklyReports()
      .then(setReports)
      .catch(() => setReports([]));
  }, []);

  // Nothing to say and no way to make one: stay off the screen entirely rather
  // than advertising a feature this setting has switched off.
  if (!reports || (reports.length === 0 && !canSummarise)) return null;

  const latest = reports[0] ?? null;
  const earlier = Math.max(0, reports.length - 1);

  return (
    <section className="stack">
      {compact ? <h3 className="section-head">Weeks</h3> : <h2>Weekly recaps</h2>}
      {!compact && (
        <p className="muted">
          What a week actually held, written from your own entries. Last Tuesday stops being
          reachable surprisingly fast, and this is how you get it back.
        </p>
      )}

      {reports.length === 0 && (
        <p className="faint">
          Nothing yet. One is written for each week once it has finished, from whatever you logged
          in it.
        </p>
      )}

      {/*
        ONE ROW, AND THE REST BEHIND A DOOR.

        This section is now a FIXED height whatever the history behind it — which
        is the actual answer to "what happens when the weeks grow long", rather
        than the answer that buys a year. A full list grew 75px a week: one extra
        screen of scrolling every two and a half months, for ever, sitting on top
        of the record it is derived from.

        The newest week is the one he wants ~every time he wants any of them, and
        the list is reverse-chronological, so every other row was scroll-past
        cost paid on every visit to reach the days.

        THE FETCH IS STILL THE WHOLE LIST, and must stay that way:
        api.weeklyReports() is the only caller of GET /insights/reports in this
        client and that route is what lazily writes each finished week. Asking it
        for less would stop weeks being written. Only the rendering is trimmed.
      */}
      {latest && (
        <div className="stack-sm stack">
          <Link className="option" to={`/week/${latest.weekStart}`}>
            <strong>{formatWeek(latest.weekStart, latest.weekEnd)}</strong>
            <span>{summarise(latest)}</span>
          </Link>
          {/* Same destination as the row above, and that is right rather than
              redundant: the week page holds the newest week open with every
              other week collapsed beneath it, so "read this one" and "see them
              all" genuinely are one place. There is no index screen, because an
              index of one-line links to pages that each list the same weeks is
              a screen that exists to be passed through. */}
          {earlier > 0 && (
            <Link className="linkish" to={`/week/${latest.weekStart}`}>
              {earlier} earlier week{earlier === 1 ? '' : 's'} ›
            </Link>
          )}
        </div>
      )}
    </section>
  );
}
